import { ReactElement } from "react";
import Input from "../../../components/Atomos/Inputs/Input";
import DropDownList from "../../../components/Atomos/DropDown/Dropdown";
import type {
  IntegratorFeedAuth,
  IntegratorFeedAuthType,
} from "../../../interfaces/models/IIntegratorFirm";

export type FeedAuthForm = {
  type: IntegratorFeedAuthType;
  username: string;
  password: string;
  token: string;
};

const AUTH_TYPE_OPTIONS: { id: IntegratorFeedAuthType; name: string }[] = [
  { id: "none", name: "No Auth" },
  { id: "basic", name: "Basic Auth" },
  { id: "bearer", name: "Bearer Token" },
];

export function emptyFeedAuthForm(): FeedAuthForm {
  return { type: "none", username: "", password: "", token: "" };
}

export function feedAuthFormFromConfig(
  auth: IntegratorFeedAuth | undefined
): FeedAuthForm {
  const type: IntegratorFeedAuthType =
    auth?.type === "basic" || auth?.type === "bearer" ? auth.type : "none";
  return {
    type,
    username: auth?.username ?? "",
    password: auth?.password ?? "",
    token: auth?.token ?? "",
  };
}

/** Persist only the fields that belong to the selected type. */
export function feedAuthFromForm(form: FeedAuthForm): IntegratorFeedAuth {
  if (form.type === "basic") {
    return {
      type: "basic",
      username: form.username.trim(),
      password: form.password,
    };
  }
  if (form.type === "bearer") {
    return { type: "bearer", token: form.token.trim() };
  }
  return { type: "none" };
}

type FeedAuthFieldsProps = {
  value: FeedAuthForm;
  onChange: (next: FeedAuthForm) => void;
  /** When true, omits the section heading for inline layout. */
  embedded?: boolean;
};

export default function FeedAuthFields({
  value,
  onChange,
  embedded = false,
}: FeedAuthFieldsProps): ReactElement {
  const content = (
    <>
      <div className="flex w-full flex-col gap-6 lg:flex-row lg:items-end">
        <div className="min-w-0 w-full lg:max-w-sm">
          <DropDownList
            title="Auth Type"
            getid
            disableInput
            data={AUTH_TYPE_OPTIONS}
            selectedObjst={
              AUTH_TYPE_OPTIONS.find((option) => option.id === value.type)?.name
            }
            seter={(option: { id: IntegratorFeedAuthType; name: string }) =>
              onChange({ ...value, type: option?.id ?? "none" })
            }
          />
        </div>
      </div>
      {value.type === "basic" && (
        <div className="flex w-full flex-col gap-6 lg:flex-row">
          <div className="min-w-0 flex-1">
            <Input
              title="Username"
              name="feedAuthUsername"
              value={value.username}
              placeholder="Username"
              onChange={(e) => onChange({ ...value, username: e.target.value })}
              error={false}
              isWfull
            />
          </div>
          <div className="min-w-0 flex-1">
            <Input
              title="Password"
              name="feedAuthPassword"
              type="password"
              value={value.password}
              placeholder="Password"
              onChange={(e) => onChange({ ...value, password: e.target.value })}
              error={false}
              isWfull
            />
          </div>
        </div>
      )}
      {value.type === "bearer" && (
        <Input
          title="Token"
          name="feedAuthToken"
          type="password"
          value={value.token}
          placeholder="Token"
          onChange={(e) => onChange({ ...value, token: e.target.value })}
          error={false}
          isWfull
        />
      )}
    </>
  );

  if (embedded) {
    return <div className="flex min-w-0 flex-1 flex-col gap-6">{content}</div>;
  }

  return (
    <section className="flex flex-col gap-6">
      <h2 className="text-xl font-bold text-gray-font">Authentication</h2>
      {content}
    </section>
  );
}
